export type PurchaseOrderPartStatus = 'pending' | 'confirmed' | 'rejected';

/** Request-level rollup: pending if any part is pending; completed when all are confirmed or rejected. */
export type PurchaseOrderStatus = 'pending' | 'completed';

export interface PurchaseOrderPart {
    /** Unique PO cost-lot line; may repeat across legacy rows as partId. UI collapses by partId. */
    lineId?: string;
    partId: string;
    inventoryItemId: string;
    name: string;
    partNumber: string;
    quantity: number;
    /** 0 Same Day, 1 Next Day, >=2 custom days */
    deliveryDays: number;
    listPrice: number;
    buyPrice: number;
    status: PurchaseOrderPartStatus;
}

/**
 * Collapse cost-lot sibling rows that share a `partId` into one row for display.
 * Preserves first-seen order; sums quantity; qty-weights buyPrice; keeps first entry's other fields.
 */
export function collapsePurchaseOrderPartsByPartId(
    parts: PurchaseOrderPart[],
): PurchaseOrderPart[] {
    const byPartId = new Map<string, PurchaseOrderPart>();
    for (const part of parts) {
        const existing = byPartId.get(part.partId);
        if (!existing) {
            byPartId.set(part.partId, { ...part });
            continue;
        }
        const quantity = existing.quantity + part.quantity;
        const buyPrice =
            quantity > 0
                ? (existing.buyPrice * existing.quantity + part.buyPrice * part.quantity) /
                  quantity
                : existing.buyPrice;
        byPartId.set(part.partId, {
            ...existing,
            quantity,
            buyPrice,
        });
    }
    return Array.from(byPartId.values());
}

export interface PurchaseOrderEntry {
    purchaseOrderId: string;
    orderId: string;
    supplierOrderId: string | null;
    status: PurchaseOrderStatus;
    roNumber: string;
    sourceLocationId: string;
    sourceLocationName: string;
    destinationLocationId: string;
    destinationLocationName: string;
    timePlaced: string;
    confirmedByEmail: string | null;
    requestorEmail: string | null;
    /** Set when the completion PO invoice PDF was generated; otherwise null. */
    invoiceNumber: string | null;
    /** Feed-only enrichment; absent on confirm/reject/submit responses and older APIs. */
    fulfillerEmail?: string | null;
    fulfillerName?: string | null;
    /** e.g. "2021 Toyota Camry" */
    vehicle?: string | null;
    parts: PurchaseOrderPart[];
}

export interface PurchaseOrdersFeedInput {
    /**
     * Part-scoped filter (not request rollup):
     * - pending: rows with ≥1 pending part; parts[] = pending only
     * - completed: rows with ≥1 confirmed/rejected part; parts[] = those only
     * - all: all rows; full parts[]
     */
    statusFilter?: PurchaseOrderStatus | 'all';
    locationIds?: string[] | 'all';
    search?: string;
    purchaseOrderId?: string;
    limit?: number;
    cursor?: string;
}

export interface PurchaseOrdersFeedResponse {
    requests: PurchaseOrderEntry[];
    nextCursor?: string;
    total: number;
}

export type SubmitPurchaseOrderPart =
    | { partId: string; action: 'confirm'; deliveryDays: number }
    | { partId: string; action: 'reject' };

export interface SubmitPurchaseOrderInput {
    purchaseOrderId: string;
    parts: SubmitPurchaseOrderPart[];
}

export interface SubmitPurchaseOrderResponse {
    purchaseOrderId: string;
    confirmedPartIds: string[];
    rejectedPartIds: string[];
}

export interface GetPurchaseOrderPdfInput {
    purchaseOrderId: string;
}

export interface GetPurchaseOrderPdfResponse {
    url: string;
    invoiceNumber: string;
}

export function deliveryDaysLabel(days: number): string {
    if (days === 0) return 'Same Day';
    if (days === 1) return 'Next Day';
    if (days >= 2) return `${days} Days`;
    return '—';
}
